import { Fragment, h } from 'preact'
import { useState } from 'preact/hooks'

import Button from 'components/Button'
import Icon from 'components/Icon'
import Modal from 'components/Modal'
import { useAppState } from 'lib/appState'

import './index.sass'
import Alert from 'components/Alert'

const SUPPORT_AMOUNTS = [
  { cents: 100, label: '$1' },
  { cents: 300, label: '$3' },
  { cents: 500, label: '$5' },
  { cents: 1000, label: '$10' },
  { cents: 2000, label: '$20' },
  { cents: 5000, label: '$50' },
  { cents: 10000, label: '$100' },
  { cents: 50000, label: '$500' },
]

export default function SupportOrganizationButton({ organization, currentUser, isAdmin }) {
  const [showModal, setShowModal] = useState(false)
  const [selectedAmount, setSelectedAmount] = useState(null)
  const [isRecurring, setIsRecurring] = useState(false)

  const hasRecurringPrices = organization.support_stripe_recurring_prices &&
    Object.keys(organization.support_stripe_recurring_prices).length > 0

  const { appAction, creatingCheckout, creatingCheckoutError } = useAppState(
    {
      [`organization:${organization.apikey}:support:creatingCheckout`]: 'creatingCheckout',
      [`organization:${organization.apikey}:support:creatingCheckoutError`]: 'creatingCheckoutError',
    },
    'SupportOrganizationButton'
  )

  const createCheckout = appAction('organizationSupport.createSupportCheckout', organization.apikey)

  if (!organization.support_enabled) {
    return null
  }

  if (!currentUser) {
    return null
  }

  if (isAdmin) {
    return null
  }

  const handleSupportClick = () => {
    setShowModal(true)
  }

  const handleAmountSelect = (amountCents) => {
    setSelectedAmount(amountCents)
    sessionStorage.setItem('supportAmount', amountCents.toString())
    sessionStorage.setItem('supportIsRecurring', isRecurring.toString())
    createCheckout(amountCents, isRecurring)
  }

  return <Fragment>
    <Button
      type="normal"
      onClick={handleSupportClick}
      className="SupportOrganizationButton-button"
      title="Support this hub"
    >
      <Icon type="heart" size={'sm'} className="SupportOrganizationButton-heart" />
      Patreon
    </Button>

    {showModal && (
      <Modal
        onClose={() => setShowModal(false)}
        className="SupportOrganizationButton-modal"
      >
        <div className="SupportOrganizationButton-content">
          <div className="SupportOrganizationButton-header">
            <Icon type="heart" className="SupportOrganizationButton-header-icon" />
            <h2>Support {organization.name}</h2>
            <p className="SupportOrganizationButton-subtitle">Your contribution makes a difference</p>
          </div>

          {organization.support_message && (
            <div className="SupportOrganizationButton-message">
              <Icon type="quote" className="SupportOrganizationButton-quote-icon" />
              <p>{organization.support_message}</p>
            </div>
          )}

          <div className="SupportOrganizationButton-payment-type">
            <div className="SupportOrganizationButton-title">Support type</div>
            <div className="SupportOrganizationButton-type-buttons">
              <Button
                type={!isRecurring ? 'primary' : 'normal'}
                onClick={() => setIsRecurring(false)}
                className="SupportOrganizationButton-type-button"
              >
                <Icon type="dollar" />
                One-time
              </Button>
              <Button
                type={isRecurring ? 'primary' : 'normal'}
                onClick={() => hasRecurringPrices && setIsRecurring(true)}
                disabled={!hasRecurringPrices}
                className="SupportOrganizationButton-type-button"
                title={!hasRecurringPrices ? 'Recurring support not available yet' : ''}
              >
                <Icon type="refresh" />
                Monthly
              </Button>
            </div>
            {!hasRecurringPrices && (
              <Alert className="SupportOrganizationButton-notice" type={'info'}>
                <span>Monthly recurring support will be available soon</span>
              </Alert>
            )}
          </div>

          <div className="SupportOrganizationButton-amounts-section">
            <div className="SupportOrganizationButton-title">Choose your support amount</div>
            <div className="SupportOrganizationButton-amounts">
              {SUPPORT_AMOUNTS.map(({ cents, label }) => (
                <Button
                  key={cents}
                  type={selectedAmount === cents ? 'primary' : 'normal'}
                  onClick={() => handleAmountSelect(cents)}
                  disabled={creatingCheckout}
                  className="SupportOrganizationButton-amount"
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {creatingCheckoutError && (
            <div className="SupportOrganizationButton-error">
              <Icon type="warning" />
              <span>{creatingCheckoutError.message || 'Failed to create checkout session. Please try again.'}</span>
            </div>
          )}

          {creatingCheckout && (
            <div className="SupportOrganizationButton-loading">
              <Icon type="spinner" />
              <div>
                <div className="SupportOrganizationButton-loading-title">Processing...</div>
                <div className="SupportOrganizationButton-loading-subtitle">Redirecting you to secure checkout</div>
              </div>
            </div>
          )}

          {!creatingCheckout && !creatingCheckoutError && (
            <div className="SupportOrganizationButton-footer">
              <Icon type="lock" className="SupportOrganizationButton-secure-icon" />
              <span>Secure payment powered by Stripe</span>
            </div>
          )}
        </div>
      </Modal>
    )}
  </Fragment>
}
